/**
 * The camera: behind and above you while walking, far enough out to see the whole planet when you
 * zoom out, and from the front for the welcome planet. The sun turns with it, so the light falls the
 * same way in every view, and its shadow box follows the planet's size.
 */
import * as THREE from 'three';

import { approach } from '@core/math';

import type { WorldScene } from './scene';

export interface CameraState {
  /** 0: following; 1: the whole planet. Eased. */
  view: number;
  /** 1 when playing (looking down at yourself when zoomed out), 0 for the welcome planet. */
  topDown: number;
}

export interface CameraInput {
  viewTarget: number;
  /** Shown whole from the front, above a card: the welcome planet. */
  front: boolean;
  /** How much of a portrait screen a bottom sheet covers, so the planet sits above it. */
  sheet?: number;
  /** How much wider than the planet the view is when it's shown whole (1 = the edges touch the screen). */
  margin?: number | undefined;
  /** What an open sheet covers, in CSS pixels: the view shifts so the planet stays clear of it. */
  inset?: { bottom: number; right: number };
  /** The hub's beacon hangs over me: the view drops so I stand just above the sheet, room above. */
  beacon?: boolean;
  playing: boolean;
  shownRadius: number;
  /** The height of the tile under you, scaled. */
  lift: number;
  width: number;
  height: number;
  reduced: boolean;
}

/** Where the following camera sits, relative to the player: up and back, growing with the planet. */
const FOLLOW_UP = 3.4;
const FOLLOW_UP_PER_R = 0.5;
const FOLLOW_BACK = 6.4;
const FOLLOW_BACK_PER_R = 0.85;
const LOOK_UP = 0.35;
/** Under the hub's beacon, how far above the sheet's top edge I stand, in CSS pixels. */
const BEACON_ROOM = 120;

/** The following camera's distance to its look point on a planet of radius `R`. */
export const followDistance = (R: number): number =>
  Math.hypot(FOLLOW_UP - LOOK_UP + R * FOLLOW_UP_PER_R, FOLLOW_BACK + R * FOLLOW_BACK_PER_R);

const _look = new THREE.Vector3();
const followPos = new THREE.Vector3();
const followLook = new THREE.Vector3();
const overLook = new THREE.Vector3();

export interface CameraRig {
  state: CameraState;
  /** The point the camera looks at (world space). */
  readonly look: THREE.Vector3;
  /** Returns whether the view is still easing (the inset) and wants more frames. */
  update(dt: number, input: CameraInput): boolean;
  /**
   * Pixels per world unit at the look point when following a player on a planet of `radius`,
   * whatever the zoom: the walker's finger scale comes from this, not from the current camera.
   */
  pxPerUnitAt(radius: number, height: number): number;
  resize(width: number, height: number): void;
}

export function createCameraRig(world: WorldScene): CameraRig {
  const { camera, sun, halo } = world;
  const state: CameraState = { view: 0, topDown: 1 };
  let offX = 0;
  let offY = 0;
  return {
    state,
    look: _look,
    resize(width, height) {
      camera.aspect = width / height;
      camera.fov = width < height ? 52 : 40;
      camera.updateProjectionMatrix();
    },
    pxPerUnitAt(radius, height) {
      return height / (2 * followDistance(radius) * Math.tan((camera.fov * Math.PI) / 360));
    },
    update(dt, input) {
      const { shownRadius: R, reduced } = input;
      state.view = reduced ? input.viewTarget : approach(state.view, input.viewTarget, 4, dt);
      const wantTop = input.front || !input.playing ? 0 : 1;
      state.topDown = reduced ? wantTop : approach(state.topDown, wantTop, 4, dt);
      const view = state.view;
      const topDown = state.topDown;
      const portrait = input.width < input.height;
      const lift0 = input.playing ? input.lift : 0;
      followPos.set(
        0,
        R + lift0 + FOLLOW_UP + R * FOLLOW_UP_PER_R,
        FOLLOW_BACK + R * FOLLOW_BACK_PER_R,
      );
      followLook.set(0, R + lift0 + LOOK_UP, 0);
      const vf = (camera.fov * Math.PI) / 180;
      const hf = 2 * Math.atan(Math.tan(vf / 2) * camera.aspect);
      const fit = Math.min(vf, hf);
      const margin = input.margin ?? (input.front ? (portrait ? 1.5 : 1.52) : 1.22);
      const dist = (R * margin) / Math.sin(fit / 2);
      const sheet = input.sheet ?? 0.42;
      overLook
        .set(0, input.front ? -R * (portrait ? sheet : 0.24) : -R * 0.06, 0)
        .multiplyScalar(1 - topDown);
      const fy = followPos.y - followLook.y;
      const fz = followPos.z - followLook.z;
      const fAng = Math.atan2(fz, fy);
      const fDist = Math.hypot(fy, fz);
      const oAng = Math.atan2(0.96, 0.29) * (1 - topDown);
      const ang = fAng + (oAng - fAng) * view;
      const camD = fDist + (dist - fDist) * view;
      _look.lerpVectors(followLook, overLook, view);
      camera.position.set(_look.x, _look.y + Math.cos(ang) * camD, _look.z + Math.sin(ang) * camD);
      camera.up.set(0, Math.sin(ang), -Math.cos(ang));
      camera.lookAt(_look);
      // the glow and the sun's shadow box follow the planet's size
      const D = camera.position.length();
      const eff = (R * D) / Math.sqrt(Math.max(1e-6, D * D - R * R));
      halo.scale.setScalar((eff * 2) / 0.635);
      // an open sheet: shift the frame so the planet sits in the middle of the rest of the screen,
      // above (or beside) it, at the same zoom
      const wantX = Math.min((input.inset?.right ?? 0) * 0.5, input.width * 0.2);
      const covered = input.inset?.bottom ?? 0;
      const wantY = input.beacon
        ? Math.max(
            -input.height * 0.22,
            Math.min(covered + BEACON_ROOM - input.height / 2, input.height * 0.24),
          )
        : Math.min(covered * 0.5, input.height * 0.45);
      offX = reduced ? wantX : approach(offX, wantX, 6, dt);
      offY = reduced ? wantY : approach(offY, wantY, 6, dt);
      if (Math.abs(offX) > 0.5 || Math.abs(offY) > 0.5)
        camera.setViewOffset(input.width, input.height, offX, offY, input.width, input.height);
      else if (camera.view?.enabled) camera.clearViewOffset();
      const sd = R + 3;
      const turnA = ang - fAng;
      const ca = Math.cos(turnA);
      const sa = Math.sin(turnA);
      sun.position
        .set(-0.42, 0.72 * ca - 0.55 * sa, 0.72 * sa + 0.55 * ca)
        .normalize()
        .multiplyScalar(sd + 12);
      sun.target.position.set(0, 0, 0);
      const sc = sun.shadow.camera;
      if (sc.right !== sd) {
        sc.left = -sd;
        sc.right = sd;
        sc.top = sd;
        sc.bottom = -sd;
        sc.near = 1;
        sc.far = sd * 2 + 30;
        sc.updateProjectionMatrix();
      }
      return Math.abs(offX - wantX) > 0.5 || Math.abs(offY - wantY) > 0.5;
    },
  };
}
